<template>
    <nav
        v-if="content?.length > 0"
        v-click-outside="closeAll"
        :class="{
            [`theme-${theme}`]: true,
        }"
    >
        <template v-if="typeof(id) !== 'string'">
            <div
                class="mobile-background"
                :class="{
                    'open': isMobileOpen,
                }"
                @click="isMobileOpen = false"
            ></div>
            <button
                class="mobile"
                :class="{
                    'open': isMobileOpen,
                }"
                @click="isMobileOpen = !isMobileOpen"
            >
                <DSIcon code="hamburger" size="xl" />
                <DSIcon code="close" />
            </button>
        </template>
        <ul key="nav" class="list">
            <template
                v-for="item, key in content"
                :key="`item-${item.id}-${key}`"
            >
                <li
                    class="item"
                    :class="{
                        'selected': isSelected(item),
                        'open': item.id === openItem,
                    }"
                >
                    <nuxt-link :to="$cms.localizeLink(item.url)">{{ item.text }}</nuxt-link>
                    <button
                        v-if="item.children?.length > 0"
                        @click="openItem = item.id !== openItem ? item.id : 0; $emit('select', openItem)"
                    >
                        <DSIcon
                            size="xs"
                            code="chevron-down"
                            :aria-label="$t('navigation.openSubmenu', { parent: item.text })"
                        />
                    </button>
                </li>
                <LayoutNavigation
                    :id="String(openItem)"
                    class="sub-level"
                    :content="item.children"
                    :class="{
                        'open': item.id === openItem,
                    }"
                    :inert="item.id !== openItem"
                    @select="value => openItem = value"
                />
            </template>
            <template v-if="typeof(id) !== 'string'">
                <div v-if="props.extrasInMenu" class="add-nav">
                    <nuxt-link
                        v-for="link, key in links"
                        :key="`link-${key}`"
                        v-bind="link"
                    >
                        <DSIcon v-if="link.icon" :code="link.icon" />{{ link.title }}
                    </nuxt-link>
                </div>
                <LayoutLanguage />
            </template>
        </ul>
    </nav>
</template>
<script setup>
import { ref, watch, inject } from 'vue';
import { useRoute, useNuxtApp, storeToRefs } from '#imports';
import vClickOutside from '~dsc/directives/click-outside';
import { useLayoutStore } from '~dsc/stores/cms/layout';

const { $cms } = useNuxtApp();
const route = useRoute();
const theme = inject('theme');
const openItem = ref('');

const props = defineProps({
    id: {
        type: String,
        required: false,
        default: null,
    },
    content: {
        type: Array,
        required: false,
        default: () => [],
    },
    links: {
        type: Array,
        required: false,
        default: () => [],
    },
    extrasInMenu: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const { isMobileOpen } = await storeToRefs(useLayoutStore());

defineEmits(['select']);
function isSelected(item) {
    const currentRoute = $cms.localizeLink(`${route.path.replace(/^\//, '')}`);

    return $cms.localizeLink(item.url) === currentRoute
        || item.children?.map(item => $cms.localizeLink(item.url))?.includes(currentRoute);
}
function closeAll() {
    if (!props.id) {
        openItem.value = '';
    }
}

watch(() => route.path, () => {
    openItem.value = '';
    isMobileOpen.value = false;
});
</script>
<style scoped>
nav {
  width: 100%;
  display: flex;
  position: relative;
  flex-flow: row nowrap;
  justify-content: center;
  pointer-events: none;
}
@media (max-width: 1199px) {
  nav {
    flex-flow: column wrap;
  }
}
@media (min-width: 1200px) {
  nav {
    border-top: 1px solid var(--color-base-grey-200);
    border-bottom: 1px solid var(--color-base-grey-200);
    padding: 0 1.5rem;
  }
}
nav.theme-black * {
  color: var(--color-base-white);
}
nav.theme-black .item.selected {
  color: var(--color-secondary-500);
}
nav.theme-black .item.open button .icon {
  color: var(--color-secondary-500);
}
nav.theme-white.main-nav .item.open::after {
  background: var(--color-primary-500);
}
nav.theme-white .sub-level {
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(1rem);
}
nav * {
  pointer-events: auto;
}
nav .list {
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  width: 100%;
  margin: 0;
}
@media (max-width: 1199px) {
  nav .list {
    position: fixed;
    width: calc(100% - 2rem);
    left: 0;
    top: 4rem;
    padding-top: 1rem;
    height: calc(100% - 4rem);
    overflow-y: scroll;
    background: var(--color-base-white);
    align-content: flex-start;
    transition: transform 0.5s ease;
    transform: translateX(-100%);
  }
  nav .list > :last-child {
    margin-bottom: auto;
  }
}
nav .mobile-background {
  transition: 0.5s all ease;
  position: fixed;
  top: 4rem;
  left: 0;
  width: 100%;
  height: calc(100vh - 4rem);
  opacity: 0;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(20px);
}
nav .mobile-background.open {
  pointer-events: auto;
  opacity: 1;
}
nav .item {
  display: inline-flex;
  align-items: center;
  text-transform: uppercase;
  position: relative;
  line-height: 1.33em;
}
nav .item > a {
  transition: all 0.2s ease-in-out;
  text-align: left;
  line-height: 1.33em;
  color: var(--color-primary-white);
  margin: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}
nav .item > a.ds-btn--active {
  color: var(--color-secondary-500);
}
@media (max-width: 1199px) {
  nav .item > a {
    margin: 1rem 0;
  }
}
nav .item a:focus-visible {
  outline: var(--color-base-grey-800) auto 1px;
}
nav .item * {
  text-decoration: none;
}
@media (min-width: 1200px) {
  nav .item::after {
    transition: all 0.2s ease-in-out;
    position: absolute;
    content: "";
    height: 0;
    bottom: 0;
    left: 50%;
    width: 100%;
    transform-origin: center center;
    transform: translateX(-50%) scaleX(0);
    background: var(--color-secondary-500);
  }
  nav .item.open::after {
    height: 0.25rem;
    transform: translateX(-50%) scaleX(1);
  }
  nav .item:not(:last-child) {
    margin-right: 2rem;
  }
}
@media (max-width: 1199px) {
  nav .item {
    position: relative;
    width: 100%;
    justify-content: space-between;
    padding: 0 1.5rem;
  }
  nav .item::before {
    transition: all 0.2s ease-in-out;
    content: "";
    position: absolute;
    width: 0.25rem;
    height: 100%;
    left: 0;
    top: 0;
    transform: scaleY(0);
    transform-origin: center top;
    background: var(--color-secondary-500);
  }
}
nav .item button {
  display: inline-flex;
  align-items: center;
  margin-left: 0.5rem;
  -webkit-tap-highlight-color: transparent;
}
nav .item button .icon {
  transition: all 0.2s ease-in-out;
  position: relative;
  top: -0.125rem;
}
nav .item button:focus-visible {
  outline: var(--color-base-grey-800) auto 1px;
}
nav .item.open button .icon {
  transform: rotate(-180deg);
}
@media (max-width: 991px) {
  nav .item.open::before {
    transform: scaleY(1);
  }
}
nav .mobile {
  display: inline-grid;
  grid-template-rows: 1fr;
  grid-template-columns: 1fr;
  align-self: start;
  align-items: center;
  justify-items: center;
  position: relative;
  -webkit-tap-highlight-color: transparent;
}
nav .mobile > * {
  transition: all 0.2s ease;
  opacity: 0;
  transform: scale(0);
  grid-column-start: 1;
  grid-row-start: 1;
}
nav .mobile:active {
  background: none;
}
nav .mobile:not(.open) > :first-child {
  opacity: 1;
  transform: scale(1);
}
nav .mobile.open > :last-child {
  opacity: 1;
  transform: scale(1);
}
nav .mobile.open + .list {
  transform: translateX(0);
}
@media (min-width: 1200px) {
  nav .mobile {
    display: none;
  }
}
nav .sub-level {
  transition: all 0.3s cubic-bezier(0.3, 0.95, 0, 1);
}
@media (min-width: 1200px) {
  nav .sub-level {
    position: absolute;
    bottom: 0;
    z-index: 1;
    padding: 2rem 1.5rem;
    transform: translateY(100%) scaleY(0);
    transform-origin: center top;
    opacity: 0;
  }
  nav .sub-level::before {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    content: "";
    opacity: 0.8;
  }
  nav .sub-level.open {
    transition: all 0.3s cubic-bezier(0, 1, 0.3, 0.95);
    opacity: 1;
    transform: translateY(100%) scaleY(1);
  }
}
@media (max-width: 1199px) {
  nav .sub-level {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 0fr;
    opacity: 0;
    transition: 0.5s all ease;
  }
  nav .sub-level .list {
    overflow: hidden;
    position: static;
    width: auto;
    height: auto;
    padding: 0;
    transform: none;
    padding-left: 2rem;
  }
  nav .sub-level.open {
    grid-template-rows: 1fr;
    opacity: 1;
  }
}
nav .sub-level .item {
  text-transform: none;
}
nav .add-nav {
  padding: 1.5rem;
  display: flex;
  flex-flow: row wrap;
  width: 100%;
  justify-content: flex-start;
  border-style: solid;
  border-color: var(--color-base-grey-200);
  border-width: 1px 0;
  margin: 1rem 0;
}
nav .add-nav a {
  display: inline-flex;
  justify-content: center;
}
nav .add-nav a .icon {
  margin-right: 0.5rem;
}
nav .language,
nav .quicklink,
nav .search {
  padding: 1rem 1.5rem;
}
@media (max-width: 1199px) {
  nav .hidden-mobile {
    display: none;
  }
}
@media (min-width: 1200px) {
  nav .add-nav, nav .language, nav .search, nav .quicklink {
    display: none;
  }
}
</style>
